import { useState } from "react";
import { VEHICLES } from "../lib/constants";
import { Users, Crown, ArrowUpRight, Images } from "lucide-react";
import { Lightbox } from "./Lightbox";

const Card = ({ v, onBook, onOpenGallery }) => {
  const isQuote = v.quoteOnly;
  const galleryCount = v.gallery?.length || 1;
  return (
    <div
      data-testid={`fleet-card-${v.id}`}
      className={`group relative bg-[#0A0A0A] border border-zinc-800 overflow-hidden flex flex-col hover:border-zinc-600 transition-all duration-500`}
    >
      <button
        type="button"
        onClick={() => onOpenGallery(v.id)}
        data-testid={`fleet-gallery-${v.id}`}
        className="relative h-72 overflow-hidden block w-full cursor-zoom-in"
        aria-label={`Open ${v.name} gallery`}
      >
        <img
          src={v.image}
          alt={v.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
        {isQuote && (
          <div className="absolute top-4 left-4 bg-white text-black px-3 py-1.5 text-[10px] font-bold tracking-[0.25em] uppercase flex items-center gap-2">
            <Crown className="w-3 h-3" /> Quote Only
          </div>
        )}
        {!isQuote && (
          <div className="absolute top-4 left-4 bg-[#E50914] text-white px-3 py-1.5 text-[10px] font-bold tracking-[0.25em] uppercase">
            Hourly · Packages
          </div>
        )}
        {galleryCount > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/80 backdrop-blur-sm text-white px-3 py-1.5 text-[10px] font-bold tracking-[0.25em] uppercase flex items-center gap-2 border border-zinc-700 group-hover:border-[#E50914] transition-colors">
            <Images className="w-3.5 h-3.5" />
            {galleryCount} Photos
          </div>
        )}
      </button>

      <div className="p-7 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-2xl uppercase tracking-tight leading-tight">
              {v.name}
            </h3>
            <p className="text-xs text-zinc-500 mt-2 tracking-wide">{v.tagline}</p>
          </div>
          <div className="flex items-center gap-1.5 text-zinc-500">
            <Users className="w-4 h-4" />
            <span className="text-xs font-mono-data">{v.seats}</span>
          </div>
        </div>

        {!isQuote ? (
          <div className="mt-6 space-y-2 border-t border-zinc-900 pt-5">
            {v.tiers.map((t) => (
              <div key={t.label} className="flex items-center justify-between">
                <span className="text-[11px] tracking-[0.2em] text-zinc-500 uppercase">
                  {t.label}
                </span>
                <span className="font-mono-data text-[#E50914] font-bold">
                  ${t.rate}<span className="text-zinc-500 font-normal">/hr</span>
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-3 mt-3 border-t border-dashed border-zinc-900">
              <span className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
                Wedding Pkg
              </span>
              <span className="font-mono-data text-white">${v.wedding.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
                Prom Pkg
              </span>
              <span className="font-mono-data text-white">${v.prom.toLocaleString()}</span>
            </div>
          </div>
        ) : (
          <div className="mt-6 border-t border-zinc-900 pt-5 flex-1">
            <div className="text-[11px] tracking-[0.25em] text-zinc-500 uppercase mb-4">
              Best For
            </div>
            <div className="flex flex-wrap gap-2">
              {v.bestFor.map((b) => (
                <span
                  key={b}
                  className="text-xs text-zinc-300 border border-zinc-800 px-3 py-1.5 tracking-wide"
                >
                  {b}
                </span>
              ))}
            </div>
            <p className="text-sm text-zinc-400 mt-6 leading-relaxed">
              Custom pricing tailored to your event. Premium experience with chauffeur, decor and
              red carpet on request.
            </p>
          </div>
        )}

        <button
          onClick={() => onBook(v.id)}
          data-testid={`fleet-book-${v.id}-btn`}
          className={`mt-7 group/btn flex items-center justify-between px-5 py-4 text-sm font-bold uppercase tracking-[0.2em] transition-all duration-300 ${
            isQuote
              ? "bg-white text-black hover:bg-[#E50914] hover:text-white"
              : "bg-[#E50914] text-white hover:bg-[#FF2A2A] hover:shadow-[0_0_24px_rgba(229,9,20,0.4)]"
          }`}
        >
          {isQuote ? "Request a Quote" : "Book This Vehicle"}
          <ArrowUpRight className="w-4 h-4 group-hover/btn:rotate-45 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export const Fleet = ({ onBook }) => {
  const [galleryVehicleId, setGalleryVehicleId] = useState(null);
  const galleryVehicle = VEHICLES.find((v) => v.id === galleryVehicleId);

  return (
    <section
      id="fleet"
      data-testid="fleet-section"
      className="relative py-24 md:py-32 border-t border-zinc-900"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.3em] text-[#E50914] uppercase mb-4">
              ⏤ The Fleet
            </div>
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-none">
              Choose your <span className="text-[#E50914]">arrival.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-zinc-400 leading-relaxed">
              Pricing includes a professional chauffeur and standard amenities. Fuel, tolls, and
              gratuity not included. Wedding and Prom packages include 5 hours, red carpet service,
              decor options, and signature touches.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {VEHICLES.map((v) => (
            <Card
              key={v.id}
              v={v}
              onBook={onBook}
              onOpenGallery={(id) => setGalleryVehicleId(id)}
            />
          ))}
        </div>
      </div>

      <Lightbox
        images={galleryVehicle?.gallery || []}
        name={galleryVehicle?.name || ""}
        openIndex={galleryVehicleId ? 0 : null}
        onClose={() => setGalleryVehicleId(null)}
      />
    </section>
  );
};
